import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Paperclip } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { useUploadTaskFieldValueFile } from '../hooks/useTaskFieldValueFileMutation';
import { parseFieldValueFile, buildFileUrl } from '../utils/fieldValueFile';

const ALLOWED_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
].join(',');

interface FileFieldValueEditorProps {
  value: string | null;
  fieldId: string;
  taskId: string;
  disabled?: boolean;
}

/** Views the current `Files` value and replaces it via the dedicated upload endpoint. */
export function FileFieldValueEditor({
  value,
  fieldId,
  taskId,
  disabled,
}: FileFieldValueEditorProps) {
  const { t } = useTranslation('customFields');
  const inputRef = useRef<HTMLInputElement>(null);
  const { mutate: upload, isPending } = useUploadTaskFieldValueFile(taskId);

  const file = parseFieldValueFile(value);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0];
    if (selected) upload({ fieldId, file: selected });
    e.target.value = '';
  }

  return (
    <div
      className="flex items-center gap-2"
      onClick={(e) => e.stopPropagation()}
      data-testid="custom-fields-value-file-trigger"
    >
      {file ? (
        <a
          href={buildFileUrl(file.storageKey)}
          target="_blank"
          rel="noopener noreferrer"
          className="truncate text-sm text-foreground hover:underline"
        >
          {file.fileName}
        </a>
      ) : (
        <span className="text-sm text-muted-foreground">{t('fieldValue.upload.empty')}</span>
      )}

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            disabled={disabled || isPending}
            onClick={() => inputRef.current?.click()}
            aria-label={file ? t('fieldValue.upload.replace') : t('fieldValue.upload.dropzone')}
            data-testid="custom-fields-value-file-upload-button"
          >
            <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {isPending
            ? t('fieldValue.upload.uploading')
            : file
              ? t('fieldValue.upload.replace')
              : t('fieldValue.upload.dropzone')}
        </TooltipContent>
      </Tooltip>

      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_MIME_TYPES}
        className="hidden"
        onChange={handleFileChange}
        data-testid="custom-fields-value-file-input"
      />
    </div>
  );
}
